import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import { StyledSnackbar, useSnackbar } from "../../common/AppSnackbar";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SendIcon from "@mui/icons-material/Send";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ChatMessageInput from "../../TipTap/ChatMessageInput";
import useAttachments, { buildAttachmentBlock, separateAttachments, isVisionCapableModel, ACCEPT_STRING } from "../useAttachments";
import AttachmentStrip from "../AttachmentStrip";
import { getSmartScheduleSuggestion } from "./conversationUtils";

function ScheduleDialog({ open, onClose, onSchedule }) {
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("16:00");

  useEffect(() => {
    if (open) {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");
      setSelectedDate(`${yyyy}-${mm}-${dd}`);
      setSelectedTime("16:00");
    }
  }, [open]);

  const timeZone = useMemo(() => {
    try {
      return Intl.DateTimeFormat()
        .resolvedOptions()
        .timeZone.replace(/_/g, " ");
    } catch {
      return "";
    }
  }, []);

  const handleSchedule = () => {
    if (!selectedDate || !selectedTime) return;
    const [year, month, day] = selectedDate.split("-").map(Number);
    const [hours, minutes] = selectedTime.split(":").map(Number);
    const scheduledDate = new Date(year, month - 1, day, hours, minutes, 0);
    if (scheduledDate <= new Date()) return;
    onSchedule(scheduledDate);
  };

  const isValid = (() => {
    if (!selectedDate || !selectedTime) return false;
    const [year, month, day] = selectedDate.split("-").map(Number);
    const [hours, minutes] = selectedTime.split(":").map(Number);
    return new Date(year, month - 1, day, hours, minutes) > new Date();
  })();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: "10px",
          minWidth: 340,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 0,
          pt: 2,
          px: 2.5,
        }}
      >
        <Box>
          <Typography
            sx={{ fontSize: "15px", fontWeight: 600, color: "text.primary" }}
          >
            Schedule message
          </Typography>
          {timeZone && (
            <Typography
              sx={{
                fontSize: "11px",
                color: "text.disabled",
                fontWeight: 400,
                mt: 0.25,
              }}
            >
              {timeZone}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: "text.disabled", "&:hover": { backgroundColor: "action.hover" } }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2, pb: 1, px: 2.5 }}>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <TextField
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            size="small"
            InputProps={{
              sx: {
                borderRadius: "6px",
                fontSize: "13px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "divider",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "text.disabled",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "text.secondary",
                },
              },
            }}
            inputProps={{ min: new Date().toISOString().split("T")[0] }}
            sx={{ flex: 1 }}
          />
          <TextField
            type="time"
            value={selectedTime}
            onChange={(e) => setSelectedTime(e.target.value)}
            size="small"
            InputProps={{
              sx: {
                borderRadius: "6px",
                fontSize: "13px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "divider",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "text.disabled",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "text.secondary",
                },
              },
            }}
            sx={{ flex: 1 }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2, pt: 1, gap: 0.5 }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: "none",
            color: "text.secondary",
            fontSize: "13px",
            fontWeight: 500,
            borderRadius: "6px",
            px: 1.5,
            "&:hover": { backgroundColor: "action.hover", color: "text.primary" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSchedule}
          disabled={!isValid}
          variant="contained"
          disableElevation
          sx={{
            textTransform: "none",
            backgroundColor: "text.primary",
            fontSize: "13px",
            fontWeight: 500,
            borderRadius: "6px",
            px: 1.5,
            "&:hover": { backgroundColor: "text.secondary" },
            "&.Mui-disabled": { backgroundColor: "action.selected", color: "text.disabled" },
          }}
        >
          Schedule
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MessageComposer({
  onSend,
  onSchedule,
  onStop,
  disabled,
  isThinking,
  isUnderReview = false,
  underReviewMessage = "",
  allowScheduling = true,
  allowStop = true,
  agentId,
  agentModel,
  agents,
}) {
  const [isEmpty, setIsEmpty] = useState(true);
  const [scheduleAnchor, setScheduleAnchor] = useState(null);
  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const snackbar = useSnackbar();
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const dragCounter = useRef(0);

  const {
    attachments,
    hasAttachments,
    isUploading,
    warning: attachmentWarning,
    fileInputRef,
    removeAttachment,
    clearAttachments,
    openFilePicker,
    handleFileInputChange,
    handleDrop: onDrop,
    handleDragOver,
  } = useAttachments(agentId);

  const smartSuggestion = useMemo(() => getSmartScheduleSuggestion(), []);

  const isVision = isVisionCapableModel(agentModel);

  const wrappedOnSend = useCallback(
    (text) => {
      if (isUploading) return;
      const { images, files } = separateAttachments(attachments, isVision);
      const block = buildAttachmentBlock(files);
      const imageUrls = images.map((a) => a.downloadUrl);
      onSend(text + block, imageUrls);
      clearAttachments();
    },
    [onSend, attachments, isUploading, clearAttachments, isVision]
  );

  const handleSmartSchedule = () => {
    const text = inputRef.current?.getText();
    if (!allowScheduling || !text || typeof onSchedule !== "function") return;
    setScheduleAnchor(null);
    const { files } = separateAttachments(attachments, isVision);
    const block = buildAttachmentBlock(files);
    onSchedule(text + block, smartSuggestion.date);
    inputRef.current?.clear();
    clearAttachments();
    snackbar.show(`Message scheduled for ${smartSuggestion.label}`, "success");
  };

  const handleCustomSchedule = (date) => {
    const text = inputRef.current?.getText();
    if (!allowScheduling || !text || typeof onSchedule !== "function") return;
    setCustomDialogOpen(false);
    setScheduleAnchor(null);
    const { files } = separateAttachments(attachments, isVision);
    const block = buildAttachmentBlock(files);
    onSchedule(text + block, date);
    inputRef.current?.clear();
    clearAttachments();
    const formatted = date.toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
    snackbar.show(`Message scheduled for ${formatted}`, "success");
  };

  const hasText = !isEmpty;

  const handleDropZoneDrop = useCallback(
    (e) => {
      dragCounter.current = 0;
      setDragOver(false);
      onDrop(e);
    },
    [onDrop]
  );

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (dragCounter.current === 1) setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDragOver(false);
    }
  }, []);

  return (
    <Box
      sx={{
        px: 2.5,
        py: 1.5,
        borderTop: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        flexShrink: 0,
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPT_STRING}
        onChange={handleFileInputChange}
        style={{ display: "none" }}
      />
      <Box
        className={`composer-drop-zone${dragOver ? " drag-over" : ""}`}
        onDrop={handleDropZoneDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        sx={{
          display: "flex",
          flexDirection: "column",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "12px",
          backgroundColor: "background.paper",
          transition: "border-color 150ms ease",
          "&:focus-within": { borderColor: "text.disabled" },
        }}
      >
        {hasAttachments && (
          <AttachmentStrip
            attachments={attachments}
            onRemove={removeAttachment}
          />
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 0.5,
            px: 1.5,
            py: 0.5,
          }}
        >
          <IconButton
            onClick={openFilePicker}
            disabled={disabled}
            size="small"
            sx={{
              mb: 0.25,
              color: "text.secondary",
              width: 28,
              height: 28,
              "&:hover": { backgroundColor: "action.hover" },
            }}
          >
            <AddIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <ChatMessageInput
            ref={inputRef}
            onSend={wrappedOnSend}
            onChange={setIsEmpty}
            placeholder={
              isUnderReview
                ? underReviewMessage || "Session is under review..."
                : isThinking
                ? "Agent is thinking..."
                : "Send a message..."
            }
            disabled={disabled}
            agents={agents}
          />
        {isThinking ? (
          allowStop ? (
            <IconButton
              onClick={onStop}
              size="small"
              sx={{
                mb: 0.25,
                color: "background.default",
                backgroundColor: "text.primary",
                borderRadius: "6px",
                width: 28,
                height: 28,
                "&:hover": { backgroundColor: "text.secondary" },
              }}
            >
              <StopCircleIcon sx={{ fontSize: 16, color: "background.default" }} />
            </IconButton>
          ) : (
            <Box
              sx={{
                width: 28,
                height: 28,
                mb: 0.25,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress
                size={14}
                thickness={5}
                sx={{ color: "text.secondary" }}
              />
            </Box>
          )
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.25 }}>
            <IconButton
              onClick={() => {
                if (hasText) {
                  inputRef.current?.send();
                } else if (hasAttachments && !isUploading) {
                  wrappedOnSend("");
                }
              }}
              disabled={!hasText && !hasAttachments}
              size="small"
              sx={{
                color: (hasText || hasAttachments) ? "background.paper" : "text.disabled",
                backgroundColor: (hasText || hasAttachments) ? "text.primary" : "transparent",
                borderRadius: (hasText || hasAttachments)
                  ? allowScheduling
                    ? "6px 0 0 6px"
                    : "6px"
                  : "6px",
                width: 28,
                height: 28,
                "&:hover": {
                  backgroundColor: (hasText || hasAttachments) ? "text.secondary" : "action.hover",
                },
              }}
            >
              <SendIcon sx={{ fontSize: 16 }} />
            </IconButton>
            {(hasText || hasAttachments) && allowScheduling && (
              <IconButton
                onClick={(e) => setScheduleAnchor(e.currentTarget)}
                size="small"
                sx={{
                  color: "background.paper",
                  backgroundColor: "text.primary",
                  borderRadius: "0 6px 6px 0",
                  borderLeft: 1,
                  borderColor: "text.secondary",
                  width: 20,
                  height: 28,
                  minWidth: 20,
                  "&:hover": { backgroundColor: "text.secondary" },
                }}
              >
                <ExpandMoreIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
        )}
        </Box>
      </Box>
      {attachmentWarning && (
        <Typography
          sx={{
            fontSize: "12px",
            color: "warning.main",
            mt: 0.5,
            px: 0.5,
          }}
        >
          {attachmentWarning}
        </Typography>
      )}

      <Popover
        open={allowScheduling && Boolean(scheduleAnchor)}
        anchorEl={scheduleAnchor}
        onClose={() => setScheduleAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: -1,
            borderRadius: "8px",
            border: "1px solid",
            borderColor: "divider",
            minWidth: 200,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ px: 1.5, pt: 1.25, pb: 0.5 }}>
          <Typography
            sx={{
              fontSize: "11px",
              color: "text.disabled",
              fontWeight: 500,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            Schedule
          </Typography>
        </Box>
        <Box
          onClick={handleSmartSchedule}
          sx={{
            px: 1.5,
            py: 0.75,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 1,
            "&:hover": { backgroundColor: "action.hover" },
          }}
        >
          <ScheduleIcon sx={{ fontSize: 15, color: "text.secondary" }} />
          <Typography
            sx={{ fontSize: "13px", fontWeight: 500, color: "text.primary" }}
          >
            {smartSuggestion.label}
          </Typography>
        </Box>
        <Box
          onClick={() => {
            setScheduleAnchor(null);
            setCustomDialogOpen(true);
          }}
          sx={{
            px: 1.5,
            py: 0.75,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderTop: "1px solid",
            borderColor: "divider",
            "&:hover": { backgroundColor: "action.hover" },
          }}
        >
          <ScheduleIcon sx={{ fontSize: 15, color: "text.disabled" }} />
          <Typography sx={{ fontSize: "13px", color: "text.primary" }}>
            Custom time...
          </Typography>
        </Box>
      </Popover>

      <ScheduleDialog
        open={allowScheduling && customDialogOpen}
        onClose={() => setCustomDialogOpen(false)}
        onSchedule={handleCustomSchedule}
      />

      <StyledSnackbar {...snackbar.snackbarProps} />
    </Box>
  );
}

export default MessageComposer;
